import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  Calendar,
  Clock,
  User,
  Mail,
  Home,
  FileText,
  Loader2,
  Sparkles
} from 'lucide-react';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [appointmentInfo, setAppointmentInfo] = useState<any>(null);
  const [bookingData, setBookingData] = useState<any>(null);

  useEffect(() => {
    const checkPayment = async () => {
      const paymentId = searchParams.get('payment_id');
      const externalReference = searchParams.get('external_reference');

      console.log('✅ Pago exitoso:', { paymentId, externalReference });

      if (externalReference) {
        try {
          const parsedData = JSON.parse(externalReference);
          console.log('📋 Datos parseados:', parsedData);
          
          setBookingData(parsedData);
          
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/appointments/find-by-payment?email=${parsedData.email}&date=${parsedData.date}&time=${parsedData.time}`
          );
          
          if (response.ok) {
            const data = await response.json();
            setAppointmentInfo(data.appointment);
            console.log('✅ Turno encontrado:', data.appointment);
          } else {
            console.log('⚠️ No se encontró el turno aún');
          }
        } catch (error) {
          console.error('Error procesando pago:', error);
        }
      }

      setLoading(false);
    };

    checkPayment();
  }, [searchParams]);

  const isLoggedIn = !!localStorage.getItem('token');

  const formatDate = (dateString: string) => {
    try {
      let year: number, month: number, day: number;
      
      if (dateString.includes('T')) {
        // Formato ISO: "2025-09-05T03:00:00.000Z"
        // ✅ EXTRAER SOLO LA FECHA (sin hora) para evitar problemas de timezone
        const datePart = dateString.split('T')[0]; // "2025-11-11"
        const parts = datePart.split('-').map(Number);
        year = parts[0];
        month = parts[1] - 1;
        day = parts[2];
      }
      // Si viene como ISO simple (YYYY-MM-DD) - EL MÁS COMÚN
      else if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          year = parseInt(parts[0]);
          month = parseInt(parts[1]) - 1; // JS months are 0-indexed
          day = parseInt(parts[2]);
        } else {
          return dateString;
        }
      } 
      // Si viene como DD/MM/YYYY
      else if (dateString.includes('/')) {
        const parts = dateString.split('/');
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
      }
      // Fallback
      else {
        return dateString;
      }
      
      // ✅ CREAR FECHA EN TIMEZONE LOCAL (sin conversión UTC)
      // Esto evita que reste/sume días por diferencia horaria
      const date = new Date(year, month, day, 12, 0, 0); // Usar mediodía para evitar problemas de DST
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        console.error('Fecha inválida:', dateString);
        return dateString;
      }
      
      return date.toLocaleDateString('es-AR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8 text-center max-w-md w-full">
          <Loader2 className="w-12 h-12 text-yellow-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-700 font-medium">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl max-w-md w-full p-8">
        
        {/* Logo de Mercado Pago */}
        <div className="mb-6 flex justify-center">
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 shadow-lg">
            <img 
              src="https://http2.mlstatic.com/storage/logos-api-admin/a5f047d0-9be0-11ec-aad4-c3381f368aaf-xl@2x.png" 
              alt="Mercado Pago" 
              className="h-8 w-auto"
              onError={(e) => {
                // Fallback si la imagen no carga
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = '<span class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Mercado Pago</span>';
                }
              }}
            />
          </div>
        </div>

        {/* Ícono de éxito */}
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>

        {/* Título */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3 text-center flex items-center justify-center gap-2">
          ¡Pago Exitoso!
          <Sparkles className="w-6 h-6 text-yellow-500" />
        </h1>
        
        <p className="text-gray-600 mb-6 text-center">
          Tu pago ha sido procesado correctamente y tu cita está confirmada.
        </p>

        {/* Información del turno */}
        {(appointmentInfo || bookingData) && (
          <div className="bg-gradient-to-br from-yellow-100/50 to-amber-100/50 backdrop-blur-sm border border-yellow-200/50 rounded-xl p-5 mb-6 shadow-lg">
            <h3 className="font-semibold text-gray-900 mb-4 text-center flex items-center justify-center gap-2">
              <Calendar className="w-5 h-5 text-yellow-600" />
              Detalles de tu cita
            </h3>
            <div className="space-y-3 text-sm">
              {appointmentInfo ? (
                <>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <User className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Profesional</span>
                      <p className="font-medium text-gray-900">{appointmentInfo.professional_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <FileText className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Servicio</span>
                      <p className="font-medium text-gray-900">{appointmentInfo.service_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <Calendar className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Fecha</span>
                      <p className="font-medium text-gray-900">
                        {formatDate(appointmentInfo.appointment_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <Clock className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Hora</span>
                      <p className="font-medium text-gray-900">{appointmentInfo.appointment_time} hs</p>
                    </div>
                  </div>
                </>
              ) : bookingData && (
                <>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <User className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Cliente</span>
                      <p className="font-medium text-gray-900">{bookingData.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <Calendar className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Fecha</span>
                      <p className="font-medium text-gray-900">{formatDate(bookingData.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <Clock className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Hora</span>
                      <p className="font-medium text-gray-900">{bookingData.time} hs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/40 backdrop-blur-sm rounded-lg p-3">
                    <Mail className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-gray-600 text-xs">Email</span>
                      <p className="font-medium text-gray-900 text-xs break-all">{bookingData.email}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Mensaje de email */}
        <div className="bg-blue-100/50 backdrop-blur-sm border border-blue-200/50 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Recibirás un email de confirmación con todos los detalles de tu cita.
            </p>
          </div>
        </div>

        {/* Botones */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/search')}
            className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Volver al inicio
          </button>
          
          {isLoggedIn ? (
            <button
              onClick={() => navigate('/dashboard/paciente')}
              className="w-full bg-white/60 backdrop-blur-sm border border-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-white/80 transition-all flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Ver mis citas
            </button>
          ) : (
            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Crear cuenta gratis
            </button>
          )}
        </div>

        {/* Nota para no registrados */}
        {!isLoggedIn && (
          <div className="mt-4 bg-purple-100/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <p className="text-xs text-purple-700 flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              Crea una cuenta para gestionar todas tus citas en un solo lugar
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;